import React, { useEffect, useState } from 'react';
import api from '@/lib/axios';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Download, Eye, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { formatDate, formatDeNumber, payerHeading } from '@/lib/utils';
import { SinglePreviewSendDialog } from '@/pages/Guardians/SinglePreviewSendDialog';

const fmtEur = (v) => new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(v || 0);
const fmtDate = (d) => (d ? formatDate(d) || '–' : '–');

const PAY_STATUS = {
  paid: ['Bezahlt', 'bg-emerald-100 text-emerald-700'],
  unpaid: ['Offen', 'bg-amber-100 text-amber-700'],
  partial: ['Teilweise', 'bg-blue-100 text-blue-700'],
};

function wertText(wert) {
  if (wert == null || wert === '') return '';
  if (typeof wert === 'string') return wert;
  return formatDeNumber(wert);
}

/**
 * Jahresübersicht der Abrechnung eines Zahlungspflichtigen.
 * compact: engere Typo für die UV-Karte.
 */
export default function GuardianBillingPreview({
  guardian,
  invoices,
  loading = false,
  compact = false,
  previewTestId = 'payer-accounts-billing-preview',
  invoicesTestId = 'payer-accounts-invoices',
}) {
  const [pdfViewUrl, setPdfViewUrl] = useState(null);
  const diesesJahr = new Date().getFullYear();
  const [year, setYear] = useState(diesesJahr);
  const [jahr, setJahr] = useState(null);

  useEffect(() => {
    if (!guardian?.id) {
      setJahr(null);
      return undefined;
    }
    let cancel = false;
    api.get(`/guardians/${guardian.id}/billing-year`, { params: { year } })
      .then((r) => {
        if (!cancel && r.data?.year === year) setJahr(r.data);
      })
      .catch(() => { if (!cancel) toast.error('Jahresübersicht konnte nicht geladen werden'); });
    return () => { cancel = true; };
  }, [guardian?.id, year]);

  const monate = jahr?.months || [];
  const invoiceRows = invoices || [];
  const strategie = jahr?.year === year ? jahr?.discount_strategy : null;

  const openPdf = async () => {
    try {
      const r = await api.get(`/guardians/${guardian.id}/billing-preview-pdf`, { responseType: 'blob' });
      setPdfViewUrl(URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' })));
    } catch {
      toast.error('PDF konnte nicht geladen werden');
    }
  };

  const downloadPdf = async () => {
    try {
      const r = await api.get(`/guardians/${guardian.id}/billing-preview-pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      const heading = payerHeading(guardian.first_name, guardian.last_name);
      a.download = heading ? `Beitragsaufstellung_${heading.replace(/\s+/g, '_')}.pdf` : 'Beitragsaufstellung.pdf';
      a.click();
      URL.revokeObjectURL(url);
    } catch { /* silent */ }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-6 text-slate-400" data-testid="billing-preview-loading">
        <Loader2 className="w-4 h-4 animate-spin mr-2" />
        <span className="text-xs">Abrechnung wird geladen…</span>
      </div>
    );
  }

  return (
    <div className={compact ? 'space-y-3' : 'space-y-5'}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-1" data-testid="billing-year-nav">
          <button
            type="button"
            data-testid="billing-year-prev"
            aria-label="Vorjahr"
            onClick={() => setYear((aktuell) => aktuell - 1)}
            className="text-xs text-slate-500 hover:text-slate-900 px-1.5 py-1 rounded hover:bg-slate-100"
          >
            ‹
          </button>
          <span data-testid="billing-year-current" className="text-xs font-semibold text-slate-900 w-12 text-center">
            {year}
          </span>
          <button
            type="button"
            data-testid="billing-year-next"
            aria-label="Folgejahr"
            onClick={() => setYear((aktuell) => aktuell + 1)}
            className="text-xs text-slate-500 hover:text-slate-900 px-1.5 py-1 rounded hover:bg-slate-100"
          >
            ›
          </button>
          <span
            data-testid="billing-month-mode"
            className="text-[11px] font-medium px-2 py-0.5 rounded bg-brand-red/10 text-brand-red"
          >
            Live-Berechnung
          </span>
        </div>
        {year === diesesJahr && guardian?.id && (
          <div className="flex flex-wrap items-center gap-1.5">
            {strategie && (
              <Badge className="border-0 bg-brand-red/10 text-brand-red">
                {strategie === 'two_stage' ? 'Mehrfächer- + Geschwisterrabatt' : 'Familienrabatt'}
              </Badge>
            )}
            <button
              type="button"
              onClick={openPdf}
              className="flex items-center gap-1 text-xs text-brand-red hover:text-brand-red border border-brand-red/20 hover:bg-brand-red/5 rounded px-2 py-1 transition-colors"
              title="Beitragsaufstellung im Browser ansehen"
              data-testid="billing-preview-view-btn"
            >
              <Eye className="h-3.5 w-3.5" /> Ansehen
            </button>
            <button
              type="button"
              onClick={downloadPdf}
              className="flex items-center gap-1 text-xs text-brand-red hover:text-brand-red border border-brand-red/20 hover:bg-brand-red/5 rounded px-2 py-1 transition-colors"
              title="Beitragsaufstellung als PDF herunterladen"
              data-testid="billing-preview-pdf-btn"
            >
              <Download className="h-3.5 w-3.5" /> PDF
            </button>
            <SinglePreviewSendDialog guardian={guardian} />
          </div>
        )}
      </div>

      {!jahr ? (
        <div className="flex items-center justify-center py-6 text-slate-400" data-testid="billing-year-loading">
          <Loader2 className="w-4 h-4 animate-spin mr-2" />
          <span className="text-xs">Jahresübersicht wird geladen…</span>
        </div>
      ) : (
        <div className="min-w-0" data-testid={previewTestId}>
          <table className={`w-full border-separate border-spacing-0 ${compact ? 'text-xs' : 'text-sm'}`} data-testid="billing-year-table">
            <thead className="sticky top-0 z-20">
              <tr className="bg-white text-left text-[10px] uppercase text-slate-500 shadow-[0_-1px_0_0_#fff]">
                {['Monat', 'Kategorie', 'Wert', 'Betrag', 'Summe (Mo)', 'Lfd'].map((label) => (
                  <th
                    key={label}
                    className={`sticky top-0 z-20 bg-white py-1.5 px-2 border-b border-slate-200 ${['Wert', 'Betrag', 'Summe (Mo)', 'Lfd'].includes(label) ? 'text-right' : ''}`}
                  >
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {monate.flatMap((monat) => (
                monat.items?.length
                  ? monat.items.map((posten, index) => (
                    <tr key={`${monat.month}-${index}`} className="[&>td]:border-b [&>td]:border-slate-100">
                      <td className="py-1.5 px-2 font-medium text-slate-700">
                        {index === 0 ? monat.month_label : ''}
                      </td>
                      <td className="py-1.5 px-2">{posten.kategorie}</td>
                      <td className="py-1.5 px-2 text-right tabular-nums text-slate-500">{wertText(posten.wert)}</td>
                      <td className="py-1.5 px-2 text-right tabular-nums">{formatDeNumber(posten.betrag)}</td>
                      <td className="py-1.5 px-2 text-right tabular-nums font-semibold">
                        {index === monat.items.length - 1 ? formatDeNumber(monat.month_total) : ''}
                      </td>
                      <td className="py-1.5 px-2 text-right tabular-nums text-slate-400">
                        {index === monat.items.length - 1 ? formatDeNumber(monat.cumulative) : ''}
                      </td>
                    </tr>
                  ))
                  : [(
                    <tr key={monat.month} className="[&>td]:border-b [&>td]:border-slate-100 text-slate-400 italic">
                      <td className="py-1.5 px-2 font-medium not-italic text-slate-700">{monat.month_label}</td>
                      <td className="py-1.5 px-2" colSpan={5}>— inaktiv —</td>
                    </tr>
                  )]
              ))}
            </tbody>
            <tfoot>
              <tr className="[&>td]:border-t-2 [&>td]:border-slate-300">
                <td colSpan={4} className="py-2 px-2 text-right font-bold">Jahres-Summe</td>
                <td className="py-2 px-2 text-right font-bold text-brand-red tabular-nums" data-testid="billing-preview-total">
                  {formatDeNumber(jahr.totals?.summe)} €
                </td>
                <td />
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      <div>
        {monate.some((monat) => monat.items?.length) && (
          <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Rechnungen</div>
        )}
        {invoiceRows.length ? (
          <table className={`w-full ${compact ? 'text-xs' : 'text-sm'}`} data-testid={invoicesTestId}>
            <thead>
              <tr className="text-left text-xs text-slate-500 border-b border-slate-200">
                <th className="py-1.5 pr-2">Rechnung</th>
                <th className="py-1.5 pr-2">Datum</th>
                <th className="py-1.5 pr-2">Fällig</th>
                <th className="py-1.5 pr-2 text-right">Betrag</th>
                <th className="py-1.5 text-right">Status</th>
              </tr>
            </thead>
            <tbody>
              {invoiceRows.map((inv) => {
                const [lbl, cls] = PAY_STATUS[inv.payment_status] || [inv.payment_status, 'bg-slate-100 text-slate-600'];
                return (
                  <tr key={inv.id} className="border-b border-slate-100 last:border-0">
                    <td className="py-1.5 pr-2 font-medium text-slate-900">{inv.invoice_number}</td>
                    <td className="py-1.5 pr-2 text-slate-500">{fmtDate(inv.invoice_date)}</td>
                    <td className="py-1.5 pr-2 text-slate-500">{fmtDate(inv.due_date)}</td>
                    <td className="py-1.5 pr-2 text-right tabular-nums">{fmtEur(inv.total_amount)}</td>
                    <td className="py-1.5 text-right">
                      <Badge className={`${cls} border-0`}>{inv.status === 'cancelled' ? 'Storniert' : lbl}</Badge>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <div className={`${compact ? 'text-xs' : 'text-sm'} text-slate-400 ${compact ? 'py-3' : 'py-6'} text-center`}>
            Keine Rechnungen vorhanden
          </div>
        )}
      </div>

      <Dialog open={!!pdfViewUrl} onOpenChange={(o) => { if (!o && pdfViewUrl) { URL.revokeObjectURL(pdfViewUrl); setPdfViewUrl(null); } }}>
        <DialogContent className="max-w-4xl h-[85vh] flex flex-col" data-testid="pdf-view-dialog">
          <DialogHeader>
            <DialogTitle>
              Beitragsaufstellung{guardian && payerHeading(guardian.first_name, guardian.last_name) ? ` \u2013 ${payerHeading(guardian.first_name, guardian.last_name)}` : ''}
            </DialogTitle>
          </DialogHeader>
          {pdfViewUrl && (
            <iframe src={pdfViewUrl} title="Beitragsaufstellung" className="flex-1 w-full rounded border border-slate-200" data-testid="pdf-view-iframe" />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
